// `common/components/woo-collapsible/woo-collapsible.tpl.html` — the fold every section of the
// criteria builder is wrapped in (plan §7 phase 3, slice 2's `edit-alert`).
//
// ⚠️ THE CONTENT IS `ng-show`, NOT `ng-if`. A closed section's children stay in the DOM with
// `display: none`, which for the criteria form means every field keeps its state and its
// validation while folded away. Rendering `null` when closed — the obvious React translation —
// would reset a section's inputs each time a user collapsed and reopened it, and would drop
// them out of the form on submit. So the children are always rendered and hidden with a class.
//
// The header actions are per-section links (Clear, Add row) whose visibility defaults to "only
// while open": `ng-if="!!action.shouldDisplay ? action.shouldDisplay() : ctrl.isOpen"`. That
// default is the part worth keeping — an action with no predicate is hidden when the section
// is folded, not always shown.
import { createElement, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react'

export interface CollapsibleAction {
  name: string
  displayName: string
  /** Defaults to "visible only while open" — see the note above. */
  shouldDisplay?: (isOpen: boolean) => boolean
  onClick: () => void
}

export interface WooCollapsibleProps {
  /**
   * The element id. Not decoration: the criteria builder's navigation scrolls to these, so a
   * section without one is a section the "Filters used" links cannot reach.
   */
  id?: string
  title: string
  /** `start-open` — the initial state only; the user's toggling wins afterwards. */
  startOpen?: boolean
  /** `disable-collapse` — the header stops responding and the section stays as it is. */
  disableCollapse?: boolean
  className?: string
  /**
   * Inline style for the ELEMENT — the contact builder's sections carry `ng-style="{ order: … }"`
   * there, which is how six template-ordered sections reach the screen in five display slots.
   */
  style?: CSSProperties
  contentClassName?: string
  actions?: readonly CollapsibleAction[]
  children?: ReactNode
}

export function WooCollapsible({
  id,
  title,
  startOpen = false,
  disableCollapse = false,
  className,
  style,
  contentClassName,
  actions = [],
  children,
}: WooCollapsibleProps): ReactElement {
  const [isOpen, setIsOpen] = useState(startOpen)

  // ⚠️ THE ELEMENT AND THE CLASS ARE BOTH REAL, AND THEY ARE DIFFERENT ELEMENTS. Upstream
  // writes `<woo-collapsible id="skills-criteria-id" class="skills">` and the component's own
  // template puts `<div class="woo-collapsible">` inside it — so the caller's id and class
  // belong on the ELEMENT and the state classes on the DIV. Collapsing the two into one div,
  // which is what this did, loses every rule written against the element type: notably
  // `_search.page.scss:227`'s `woo-collapsible { display: flex; margin-bottom: 20px }`, and a
  // 20px gap missing between nine stacked sections walks the whole form up the page.
  return createElement(
    'woo-collapsible',
    { id, ...(className === undefined ? {} : { className }), ...(style === undefined ? {} : { style }) },
    <div
      className={['woo-collapsible', isOpen ? 'open' : 'closed', disableCollapse ? 'collapse-disabled' : '']
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className="collapsible-title-container"
        role="button"
        aria-expanded={isOpen}
        tabIndex={0}
        onClick={() => {
          if (!disableCollapse) setIsOpen((open) => !open)
        }}
        onKeyDown={(event) => {
          if (disableCollapse || (event.key !== 'Enter' && event.key !== ' ')) return
          event.preventDefault()
          setIsOpen((open) => !open)
        }}
      >
        <div className="collapsible-title">{title}</div>
        {actions
          .filter((action) => (action.shouldDisplay === undefined ? isOpen : action.shouldDisplay(isOpen)))
          .map((action) => (
            <a
              key={action.name}
              href=""
              className={`collapsible-action ${action.name}`}
              onClick={(event) => {
                event.preventDefault()
                // The header toggles on click; an action inside it must not also fold the
                // section it belongs to.
                event.stopPropagation()
                action.onClick()
              }}
            >
              <span>{action.displayName}</span>
            </a>
          ))}
        <i className={`collapsible-caret fas ${isOpen ? 'fa-caret-down' : 'fa-caret-left'}`} aria-hidden="true" />
      </div>
      {/* `ng-show`, so the children stay mounted — see the note at the top. */}
      <div
        className={['collapsible-content', contentClassName].filter(Boolean).join(' ')}
        style={isOpen ? undefined : { display: 'none' }}
      >
        {children}
      </div>
    </div>,
  )
}
